import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, Check, Image as ImageIcon } from 'lucide-react';
import './GalleryUploadPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function GalleryUploadPage() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/')
    );
    
    const filesWithPreview = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9)
    }));
    
    setSelectedFiles(prev => [...prev, ...filesWithPreview]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadComplete(false);

    try {
      const BATCH_SIZE = 30; // Upload 30 images at a time
      const PARALLEL_BATCHES = 2; // Upload 2 batches in parallel
      const totalFiles = selectedFiles.length;
      let uploadedTotal = 0;

      // Split files into batches
      const batches = [];
      for (let i = 0; i < totalFiles; i += BATCH_SIZE) {
        batches.push(selectedFiles.slice(i, i + BATCH_SIZE));
      }

      // Upload batches with some parallelization
      for (let i = 0; i < batches.length; i += PARALLEL_BATCHES) {
        const parallelBatches = batches.slice(i, i + PARALLEL_BATCHES);
        
        const uploadPromises = parallelBatches.map(async (batch) => {
          const formData = new FormData();
          batch.forEach(({ file }) => {
            formData.append('files', file);
          });

          const response = await fetch(`${BACKEND_URL}/api/upload-gallery-images`, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error('Upload fehlgeschlagen');
          }

          return response.json();
        });

        const results = await Promise.all(uploadPromises);
        
        results.forEach(result => {
          uploadedTotal += result.uploaded;
        });
        
        // Update progress
        const progress = Math.round((uploadedTotal / totalFiles) * 100);
        setUploadProgress(progress);
        setUploadedCount(uploadedTotal);
      }

      setUploadComplete(true);

      // Clear selected files after successful upload
      selectedFiles.forEach(({ preview }) => URL.revokeObjectURL(preview));
      setSelectedFiles([]);

      // Redirect to home after 2 seconds with hard reload
      setTimeout(() => {
        // Force a hard reload of the homepage to clear cache
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Fehler beim Hochladen: ${error.message}\n\nBisher wurden ${uploadedCount} Bilder erfolgreich hochgeladen.`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="gallery-upload-page">
      <div className="upload-container">
        <div className="upload-header">
          <h1>🏴󠁧󠁢󠁳󠁣󠁴󠁿 Schottland Galerie - Bilder hochladen</h1>
          <p>Laden Sie Ihre schönen Schottland-Fotos hoch</p>
        </div>

        {/* Drop Zone */}
        <div
          className="drop-zone"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={48} />
          <h3>Bilder hierher ziehen oder klicken</h3>
          <p>Unterstützt: JPG, PNG, WebP</p>
          <p className="multi-select-hint">Sie können mehrere Bilder gleichzeitig auswählen!</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className="selected-files">
            <h3>Ausgewählte Bilder ({selectedFiles.length})</h3>
            <div className="preview-grid">
              {selectedFiles.map(({ id, preview, file }) => (
                <div key={id} className="preview-item">
                  <img src={preview} alt={file.name} />
                  <button
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(id);
                    }}
                  >
                    <X size={16} />
                  </button>
                  <div className="file-name">{file.name}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p>Hochladen... {uploadedCount} von {selectedFiles.length} Bildern ({uploadProgress}%)</p>
            <p className="progress-hint">⚡ Schneller Upload mit paralleler Verarbeitung</p>
          </div>
        )}

        {/* Success Message */}
        {uploadComplete && (
          <div className="success-message">
            <Check size={48} />
            <h3>Upload erfolgreich!</h3>
            <p>{uploadedCount} Bilder wurden hochgeladen</p>
            <p>Sie werden zur Startseite weitergeleitet...</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            className="btn-secondary"
            onClick={() => navigate('/')}
            disabled={uploading}
          >
            Zurück
          </button>
          <button
            className="btn-primary"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading}
          >
            <Upload size={20} />
            {uploading ? 'Hochladen...' : `${selectedFiles.length} Bilder hochladen`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GalleryUploadPage;
